// How to Play (§14.2): 6 illustrated cards — Shove, Grab & Throw, Edges (teeter / ledge / stomp),
// Dizzy, Items, Ghosts — with tips that show the live device glyphs, and "Try in Practice".
// ←/→, LB/RB, Q/E, the arrow buttons, the dots or a swipe flip through the cards.
import { Difficulty, MapId, ModeId } from '@shared/ids';
import type { GlyphAction } from '../../input/glyphs';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { HOW_TO_SCENES, illustration, type HowToScene } from '../illustrations';
import { el, replayClass, setAttr, setText } from '../ui';
import { button, closeButton } from '../widgets';

type Tip = (string | { g: GlyphAction })[];

const CARDS: Readonly<Record<HowToScene, { title: string; tag: string; tips: Tip[] }>> = {
  shove: { title: 'SHOVE', tag: 'The core verb', tips: [
    ['Press ', { g: 'shove' }, ' to lunge and BONK whoever is in front of you.'],
    ['The only way to lose is falling off — push beans over the edge!'],
    ['A lunge never carries you off by itself: you teeter instead.'],
  ] },
  grab: { title: 'GRAB & THROW', tag: 'Up, up and YEET', tips: [
    [{ g: 'grab' }, ' grabs the bean in front of you and lifts it overhead.'],
    ['Press ', { g: 'throw' }, ' again to throw — carry them to the edge first!'],
    ['Grabbed yourself? Mash any button or wiggle the stick to break free.'],
  ] },
  edges: { title: 'EDGES', tag: 'Teeter, hang, stomp', tips: [
    ['Teetering? Push back toward the arena to recover — that’s a CLUTCH!'],
    ['Hanging on the ledge? ', { g: 'jump' }, ' or push inward to climb back up.'],
    ['See fingers on the rim? Stomp them to send that bean down. CRUNCH!'],
  ] },
  dizzy: { title: 'DIZZY', tag: 'The dial that decides it', tips: [
    ['Every hit adds Dizzy (0–100). Watch the dial at the bottom.'],
    ['At 0 a shove is a nudge (3 m); at 100 it’s a launch (15 m)!'],
    ['Dizzy cools down 2.5 s after your last hit. Stars mean danger.'],
  ] },
  items: { title: 'ITEMS', tag: 'Crates from the sky', tips: [
    ['Walk into a crate to grab an item; ', { g: 'item' }, ' uses it.'],
    ['Banana, Bonk Bomb, Boxing Glove, Balloon, Plunger and Big Shake.'],
    ['A Balloon you are holding saves you from one fall.'],
  ] },
  ghosts: { title: 'GHOSTS', tag: 'Out is not over', tips: [
    ['Knocked off? You come back as a floating ghost.'],
    [{ g: 'shove' }, ' drops a pebble — its shadow shows where it lands.'],
    ['Pebble a bean off the edge and you still score!'],
  ] },
};

export function create(deps: UiDeps): LiveScreen {
  let index = 0;
  const art = el('div', { class: 'ht-art' });
  const title = el('div', { class: 'ht-title' });
  const tag = el('div', { class: 'ht-tag' });
  const tips = el('ul', { class: 'ht-tips' });
  const count = el('div', { class: 'ht-count' });
  const card = el('div', { class: 'ht-card' }, art, el('div', { class: 'ht-text' }, el('div', { class: 'ht-heading' }, title, tag), tips));
  const dots = el('div', { class: 'ht-dots', attrs: { role: 'tablist', 'aria-label': 'Cards', 'data-tabs': '' } },
    ...HOW_TO_SCENES.map((s, i) => el('button', { class: 'ht-dot', attrs: { type: 'button', role: 'tab', 'aria-label': CARDS[s].title }, on: { click: () => go(i) } })));
  const prev = button({ icon: 'arrowL', title: 'Previous card', variant: 'cream', cls: 'round ht-prev', onClick: () => go(index - 1) });
  const next = button({ icon: 'arrowR', title: 'Next card', variant: 'cream', cls: 'round ht-next', onClick: () => go(index + 1) });
  const practice = button({ label: 'TRY IN PRACTICE', icon: 'target', variant: 'mint', onClick: () => deps.actions.practice({ mapId: MapId.TEACUP, modeId: ModeId.ROYALE, bots: 2, difficulty: Difficulty.CHILL, dummies: true }) });
  const back = button({ label: 'GOT IT!', icon: 'check', variant: 'pink', autofocus: true, onClick: () => deps.nav.back() });
  const stage = el('div', { class: 'ht-stage' }, prev, card, next);
  const panel = el('div', { class: 'panel sheet with-ribbon howto-panel springy' },
    el('div', { class: 'ribbon mint', text: 'HOW TO PLAY' }), closeButton(() => deps.nav.back()),
    el('div', { class: 'sheet-body ht-body' }, stage, dots, count),
    el('div', { class: 'sheet-foot row center wrap' }, practice, back));
  const root = el('div', { class: 'screen modal' }, panel);

  let touchX = -1;
  card.addEventListener('pointerdown', (e) => {
    touchX = e.clientX;
  });
  card.addEventListener('pointerup', (e) => {
    if (touchX < 0) return;
    const dx = e.clientX - touchX;
    touchX = -1;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
  });

  function render(dir: number): void {
    const scene = HOW_TO_SCENES[index];
    const c = CARDS[scene];
    art.replaceChildren(illustration(scene));
    setText(title, c.title);
    setText(tag, c.tag);
    tips.replaceChildren(...c.tips.map((t) => el('li', null, ...t.map((part) => (typeof part === 'string' ? el('span', { text: part }) : actionCap(deps, part.g))))));
    setText(count, `${index + 1} / ${HOW_TO_SCENES.length}`);
    [...dots.children].forEach((d, i) => setAttr(d, 'aria-selected', String(i === index)));
    if (dir !== 0) replayClass(card, dir > 0 ? 'slide-l' : 'slide-r');
  }

  function go(i: number): void {
    const n = HOW_TO_SCENES.length;
    const nextIndex = ((i % n) + n) % n;
    const dir = i > index ? 1 : -1;
    index = nextIndex;
    deps.services.playUi('cardFlip');
    render(dir);
  }

  deps.services.input.onChange(() => {
    if (!root.hidden) render(0);
  });

  return {
    el: root,
    onShow() {
      index = 0;
      render(0);
    },
    onNav(action: NavAction) {
      if (action === 'tabL' || action === 'tabR') {
        go(index + (action === 'tabL' ? -1 : 1));
        return true;
      }
      return false;
    },
  };
}
